import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Drawer, Grid, Skeleton } from '@mui/material';
import logo_service from '../../../assets/images/logo-sddea.png';
import ContractAttachementForm from '../../../components/forms/contract-attachement/contract-attachement.form';
import { ImageAdmin } from '../../../components/commun/image-admin';
import { getPageContent, toPageContentDTO } from '../../../services/page.service';
import { showAxiosMessageError } from '../../../services/error-message.service';
import { PageContentDto } from '../../../services/dtos/page-content.dto';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { ContractFormInfosDto } from '../../../services/dtos/form/contract-form-infos.dto';
import useRecaptcha from '../../../hooks/google-recaptcha.hook';
import { AxiosError } from 'axios';
import { paymentLoggedOff } from '../../../services/payment.service';
import { MeterReadingLoggedOffDto } from '../../../services/dtos/meter-reading-logged-off.dto';
import { retrieveLogin } from '../../../services/account.service';

const loginForgot = 'login-forgot';

/**
 * Page de Paiement facture
 * @constructor
 */
export default function CommunLoggedOffPage(props: { page: string }) {
    //Navigate
    const navigate = useNavigate();
    //SnackBar
    const showMessage = useSnackbars();
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);

    // Déclaration du loader pour l'affichage du Skeleton
    const [loading, setLoading] = useState<boolean>(true);
    // Bool pour éviter le spam du bouton Valider
    const [validate, setValidate] = useState(false);
    // Gestion de l'état du drawer
    const [drawerOpened, setDrawerOpened] = React.useState(false);
    //Création de données par défaut pour la page
    const [pageContent, setPageContent] = useState<PageContentDto>({
        content: undefined,
        title: undefined,
        image: '../../../assets/images/logo-sddea.png',
    });

    let keyCaptcha;
    let keyTitle: string;
    if (props.page === 'payment') {
        keyCaptcha = 'paiement';
        keyTitle = 'paiement';
    } else if (props.page === loginForgot) {
        keyCaptcha = 'identifiantperdu';
        keyTitle = 'identifiant-perdu';
    } else {
        keyCaptcha = 'releveindex';
        keyTitle = 'releve-index';
    }

    //Recaptcha
    const { getRecaptchaToken } = useRecaptcha(keyCaptcha);

    /**
     * Comportement à la soumission du formulaire
     * @param e Infos du form
     */
    const handlerSubmit = (e: ContractFormInfosDto) => {
        // Désactivation du bouton Valider
        setValidate(true);
        //Récupération du captcha
        getRecaptchaToken()
            .then(token => {
                //Si on vient du paiement ligne alors on demande l'adresse de paiement
                if (props.page === 'payment') {
                    //On met à jour le recaptcha
                    paymentLoggedOff(e, token)
                        .then(url => {
                            //Redirection sur l'url si fournit
                            if (url) {
                                window.open(url, '_blank', 'noreferrer');
                            }
                        })
                        .catch((error: Error | AxiosError) => {
                            showAxiosMessageError(error, showMessage);
                        })
                        .finally(() => {
                            setValidate(false);
                        });
                } else if (props.page === loginForgot) {
                    const data = {
                        montantDu: e.montantFacture,
                        referenceFacture: e.referenceFacture?.trim(),
                        referenceAbonnement: e.referenceContrat?.trim(),
                        captcha: token,
                    };
                    retrieveLogin(data)
                        .then(response => {
                            localStorage.setItem('login', response.identifiant);
                            showMessage.successAlert(`Votre identifiant de connexion est : ${response.identifiant}`);
                            navigate('/connexion');
                        })
                        .catch((error: Error | AxiosError) => {
                            showAxiosMessageError(error, showMessage);
                        })
                        .finally(() => {
                            setValidate(false);
                        });
                } else {
                    const data: MeterReadingLoggedOffDto = {
                        montantDu: e.montantFacture,
                        referenceFacture: e.referenceFacture,
                        referenceAbonnement: e.referenceContrat,
                        captcha: token,
                    };
                    if (e.email !== undefined && e.email !== '') {
                        data.email = e.email;
                    }
                    localStorage.setItem('meterReadingInfos', JSON.stringify(data));
                    navigate('/releve-compteur/validation');
                }
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
                setValidate(false);
            });
    };

    /**
     * Comportement à l'annulation du formulaire
     */
    const handlerCancel = () => {
        // Retour à la page d'accueil
        navigate('/');
    };

    // Gestion de l'ouverture du Drawer
    const toggleDrawer = (open: boolean) => () => {
        setDrawerOpened(open);
    };

    // Récupère les données du back permettant de construire la page
    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;
        //Récupération des infos de la page paiement
        getPageContent(keyTitle)
            .then(async result => {
                //On met le résultat de la request dans la variable envoyée à la page
                if (result) {
                    setPageContent(await toPageContentDTO(result));
                }
                setLoading(false);
            })
            .catch(error => {
                //Affichage du message d'erreur si la request ne récupère rien
                console.error('Erreur lors de la récupération du contenu de la page');
                showAxiosMessageError(error, showMessage);
            });

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container spacing={2}>
            {/*Affichage de l'image dans un drawer pour les écrans mobile*/}
            <Grid item xs={12} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {loading ? (
                    // Skeleton pour le chargement de l'image, rend indispo le drawer tant que l'image est pas récupérée
                    <Skeleton variant="text">
                        <Button onClick={toggleDrawer(true)}>Voir un exemple de facture</Button>
                    </Skeleton>
                ) : (
                    pageContent?.image && (
                        <>
                            {/*Drawer pour la version mobile*/}
                            <Button onClick={toggleDrawer(true)}>Voir un exemple de facture</Button>
                            <Drawer open={drawerOpened} anchor={'bottom'} onClose={toggleDrawer(false)}>
                                <ImageAdmin
                                    className="image-contract"
                                    src={pageContent.image}
                                    alt="Une facture du SDDEA"
                                />
                            </Drawer>
                        </>
                    )
                )}
            </Grid>
            {/*Formulaire*/}
            <Grid item md={6} xs={12}>
                <ContractAttachementForm
                    title={pageContent.title}
                    handlerSubmit={handlerSubmit}
                    validate={validate}
                    handlerCancel={handlerCancel}
                    logged={props.page === loginForgot}
                />
            </Grid>
            {/*Affichage de l'image sur le côté du formulaire pour les écrans normaux*/}
            <Grid item md={6} sx={{ display: { xs: 'none', md: 'flex' } }}>
                {loading ? (
                    <Skeleton variant="rectangular">
                        <img className="image-contract" src={logo_service} alt="Placeholder de chargement" />
                    </Skeleton>
                ) : (
                    //image
                    pageContent?.image && (
                        <ImageAdmin className="image-contract" src={pageContent.image} alt="Une facture du SDDEA" />
                    )
                )}
            </Grid>
        </Grid>
    );
}
